// Step 7 Phase 6 — adaptive variant resolution for HD/SD multi-variant delivery.
//
// Pure function: given a manifest + device class + optional user override,
// pick the best variant to load. No I/O, no side effects.
//
// Algorithm:
//   1. If override is provided AND that variant is ready, use it (user choice wins).
//   2. Otherwise compute preferred = deviceClass === 'mobile' ? 'sd' : 'hd'.
//   3. If preferred ready, use it.
//   4. Otherwise fall back to the other (sd ↔ hd). If that's ready, return with
//      a warning so the UI can hint the fallback was applied.
//   5. Final fallback: 'source' (legacy ply). Always returns a warning.
//   6. If no variant ready at all → return null. Caller falls back to legacy
//      `/api/model/:id` (mkkellogg adapter via Spark — both compatible).
//
// Splat count budget (parent plan §Fase 6): mobile preferred SD due to
// WebKit memory budget. If the SD variant has splat_count > MOBILE_BUDGET
// the resolver still picks SD (best available) but tags the warning so the
// viewer can show a soft warning. Hard reject is NOT done here — the user
// may still want to attempt the load.

import type { ModelManifest, ModelManifestVariant } from '../api/types.js';
import type { DeviceClass } from '../lib/device.js';

// Soft splat-count budgets per device class (informational only — used to
// decide whether to surface a warning, not to gate the load).
const MOBILE_SPLAT_BUDGET = 5_000_000;
const DESKTOP_SPLAT_BUDGET = 50_000_000;

export type VariantOverride = 'hd' | 'sd' | null;

export interface ResolvedVariant {
  variant: ModelManifestVariant;
  // Warnings the UI may surface to the user. Empty string when
  // the preferred variant for the device was used and within budget.
  warning:
    | ''
    | 'fallback-source'
    | 'fallback-hd-on-mobile'
    | 'fallback-sd-on-desktop'
    | 'over-budget'
    | 'override-not-ready';
}

function findReady(
  manifest: ModelManifest,
  variantName: 'hd' | 'sd' | 'source',
): ModelManifestVariant | null {
  return (
    manifest.available_variants.find((v) => v.variant === variantName && v.status === 'ready') ??
    null
  );
}

function isOverBudget(variant: ModelManifestVariant, deviceClass: DeviceClass): boolean {
  if (variant.splat_count === null) return false; // unknown → trust caller
  const budget = deviceClass === 'mobile' ? MOBILE_SPLAT_BUDGET : DESKTOP_SPLAT_BUDGET;
  return variant.splat_count > budget;
}

export function selectVariantForDevice(
  manifest: ModelManifest,
  deviceClass: DeviceClass,
  override: VariantOverride,
): ResolvedVariant | null {
  // 1. Honor explicit user override when ready.
  if (override !== null) {
    const overrideVariant = findReady(manifest, override);
    if (overrideVariant !== null) {
      return {
        variant: overrideVariant,
        warning: isOverBudget(overrideVariant, deviceClass) ? 'over-budget' : '',
      };
    }
    // Override not ready: fall through to auto-selection but tag the warning.
    const auto = autoSelect(manifest, deviceClass);
    if (auto !== null) {
      return { variant: auto.variant, warning: 'override-not-ready' };
    }
    return null;
  }
  return autoSelect(manifest, deviceClass);
}

function autoSelect(manifest: ModelManifest, deviceClass: DeviceClass): ResolvedVariant | null {
  const preferred = deviceClass === 'mobile' ? 'sd' : 'hd';
  const alternative = preferred === 'sd' ? 'hd' : 'sd';

  const preferredVariant = findReady(manifest, preferred);
  if (preferredVariant !== null) {
    return {
      variant: preferredVariant,
      warning: isOverBudget(preferredVariant, deviceClass) ? 'over-budget' : '',
    };
  }

  const altVariant = findReady(manifest, alternative);
  if (altVariant !== null) {
    const warning = deviceClass === 'mobile' ? 'fallback-hd-on-mobile' : 'fallback-sd-on-desktop';
    return { variant: altVariant, warning };
  }

  const sourceVariant = findReady(manifest, 'source');
  if (sourceVariant !== null) {
    return { variant: sourceVariant, warning: 'fallback-source' };
  }

  return null;
}

export const _testing = {
  MOBILE_SPLAT_BUDGET,
  DESKTOP_SPLAT_BUDGET,
};
